import {isURLDangerous} from './isURLDangerous';

const SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
const CONTROL_CHARS_REGEX = /[\u0000-\u001F\u007F]/;

/**
 * Determines if a provided return URL is safe to navigate to.
 * Allows only:
 * - relative paths starting with '/', '?', or '#'
 * - absolute or protocol-relative URLs that resolve to the same origin as the current window
 * Explicitly rejects dangerous schemes like javascript: and html;base64
 */
export function isSafeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Reject leading/trailing whitespace or any control characters
  if (url !== url.trim()) {
    return false;
  }

  if (CONTROL_CHARS_REGEX.test(url)) {
    return false;
  }

  // Prevent backslash confusion
  if (url.includes('\\')) {
    return false;
  }

  // Decode up to 3 times if URL-encoded
  let inspected = url;
  for (let i = 0; i < 3; i++) {
    if (!inspected.includes('%')) {
      break;
    }
    try {
      const next = decodeURIComponent(inspected);
      if (next === inspected) {
        break;
      }
      inspected = next;
    } catch {
      return false;
    }
  }

  // Re-check critical security validations after decoding
  if (inspected !== inspected.trim()) {
    return false; // Reject decoded whitespace
  }

  if (inspected.includes('\\')) {
    return false; // Reject decoded backslashes
  }

  if (CONTROL_CHARS_REGEX.test(inspected)) {
    return false;
  }

  if (isURLDangerous(url) || isURLDangerous(inspected)) {
    return false;
  }

  const hasScheme = SCHEME_REGEX.test(inspected);
  const isProtocolRelative = inspected.startsWith('//');

  if (hasScheme || isProtocolRelative) {
    try {
      const parsed = new URL(inspected, window.location.origin);

      // Reject embedded credentials in the authority component
      if (parsed.username || parsed.password) {
        return false;
      }

      return parsed.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  // Treat standard relative URLs and hashes as safe
  return inspected.startsWith('/') || inspected.startsWith('?') || inspected.startsWith('#');
}
